/**
 * MlScoreBadges — ML score, recommendation, edge, and top factors badges for LegCard
 *
 * Tier-gated: ml_score_badge, ml_recommendation, ml_top_factors and ml_predicted_edge are
 * each admin-only in featureAccess, so non-admins see none of these badges.
 */

import { Tooltip } from '@components/ui/Tooltip';
import type { UseFeatureAccessResult } from '@hooks/useFeatureAccess';
import type { GeneratedLeg } from '@parlay-picker/shared/api/picksApi';
import {
  getMlRecommendationDisplay,
  hasOpinion,
  type MlRecommendationTone,
  supportsBet,
} from '@parlay-picker/shared/mlRecommendation';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { Brain, GitCompare, TrendingUp, Zap } from 'lucide-react';
import type React from 'react';

// =============================================================================
// Types
// =============================================================================

interface MlScoreBadgesProps {
  leg: GeneratedLeg;
  featureAccess: UseFeatureAccessResult;
}

// =============================================================================
// Helpers
// =============================================================================

/** Get color classes for ML score badge based on score value */
function getScoreColor(score: number): string {
  if (score >= 70) return 'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30';
  if (score >= 50) return 'bg-yellow-500/20 text-yellow-400 ring-yellow-500/30';
  return 'bg-slate-500/20 text-slate-400 ring-slate-500/30';
}

/** Grey for `none`: a head with no opinion is not the red of an adverse call. */
const TONE_CLASS: Record<MlRecommendationTone, string> = {
  positive: 'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30',
  lean: 'bg-blue-500/20 text-blue-400 ring-blue-500/30',
  neutral: 'bg-yellow-500/20 text-yellow-400 ring-yellow-500/30',
  negative: 'bg-red-500/20 text-red-400 ring-red-500/30',
  none: 'bg-slate-500/20 text-slate-400 ring-slate-500/30',
};

/** Detect when classification and regression models disagree on bet direction */
interface SplitSignalInfo {
  recSupportsBet: boolean;
  edgeSupportsBet: boolean;
  recommendation: string;
  edge: number;
}

function detectSplitSignal(leg: GeneratedLeg): SplitSignalInfo | null {
  if (leg.mlRecommendation == null || leg.mlEdge == null || !leg.overUnder) return null;
  if (leg.mlEdge === 0) return null;
  const overUnder = leg.overUnder;
  if (overUnder !== 'Over' && overUnder !== 'Under') return null;

  // A declined head has no position, so it cannot disagree with the regression edge.
  // Reading its silence as a conflict is how no_signal produced a false split warning.
  if (!hasOpinion(leg.mlRecommendation)) return null;

  const edgeSupportsBet =
    (overUnder === 'Over' && leg.mlEdge > 0) || (overUnder === 'Under' && leg.mlEdge < 0);
  const recSupportsBet = supportsBet(leg.mlRecommendation);

  if (edgeSupportsBet === recSupportsBet) return null;

  const { label } = getMlRecommendationDisplay(leg.mlRecommendation);
  return { recSupportsBet, edgeSupportsBet, recommendation: label, edge: leg.mlEdge };
}

// =============================================================================
// Component
// =============================================================================

export function MlScoreBadges({
  leg,
  featureAccess,
}: MlScoreBadgesProps): React.ReactElement | null {
  const { isHidden } = featureAccess;

  // No ML data on this leg — render nothing
  if (leg.mlScore == null) return null;

  const score = leg.mlScore;
  const scoreHidden = isHidden('ml_score_badge');
  const recHidden = isHidden('ml_recommendation');
  const factorsHidden = isHidden('ml_top_factors');
  const edgeHidden = isHidden('ml_predicted_edge');
  const splitSignal = detectSplitSignal(leg);
  const showSplitSignal = !recHidden && !edgeHidden && splitSignal != null;

  // If even the score badge is hidden, show nothing
  if (scoreHidden) return null;

  return (
    <>
      {/* ML Score Badge — all tiers */}
      <Tooltip
        content={
          <>
            <span className="font-semibold text-purple-300">ML Confidence: {score}/100</span>
            <br />
            Machine learning model confidence score based on historical patterns, matchup data, and
            statistical edges.
          </>
        }
      >
        <span
          data-testid="ml-score-badge"
          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 cursor-help ${getScoreColor(score)}`}
        >
          <Brain className="w-3 h-3" />
          ML {score}
        </span>
      </Tooltip>

      {/* ML Recommendation Badge (ml_recommendation) */}
      {leg.mlRecommendation &&
        !recHidden &&
        (() => {
          const { label, tone, description } = getMlRecommendationDisplay(leg.mlRecommendation);
          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-purple-300">ML Rec: {label}</span>
                  <br />
                  {description}
                </>
              }
            >
              <span
                data-testid="ml-recommendation-badge"
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 cursor-help ${TONE_CLASS[tone]}`}
              >
                <Zap className="w-3 h-3" />
                {label}
              </span>
            </Tooltip>
          );
        })()}

      {/* ML Edge Badge — Elite */}
      {leg.mlEdge != null &&
        !edgeHidden &&
        (() => {
          const edgeVal = leg.mlEdge;
          const edgeSign = edgeVal >= 0 ? '+' : '';
          const edgeColor =
            edgeVal >= 0
              ? 'bg-emerald-500/20 text-emerald-400 ring-emerald-500/30'
              : 'bg-red-500/20 text-red-400 ring-red-500/30';
          const statLabel = leg.statType ? formatStatLabel(leg.statType) : 'pts';

          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-purple-300">
                    ML Edge: {edgeSign}
                    {edgeVal.toFixed(1)} {statLabel}
                  </span>
                  <br />
                  {leg.mlPredictedValue != null && (
                    <>
                      Predicted: {leg.mlPredictedValue.toFixed(1)} {statLabel} (line: {leg.line})
                      <br />
                    </>
                  )}
                  Difference between the ML model's predicted stat value and the betting line.
                  Positive edge means the model expects the stat to exceed the line.
                </>
              }
            >
              <span
                data-testid="ml-edge-badge"
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ring-1 cursor-help ${edgeColor}`}
              >
                <TrendingUp className="w-3 h-3" />
                Edge {edgeSign}
                {edgeVal.toFixed(1)}
              </span>
            </Tooltip>
          );
        })()}

      {/* Split Signal Badge — when classification and regression disagree */}
      {showSplitSignal &&
        (() => {
          const edgeSign = splitSignal.edge >= 0 ? '+' : '';
          const overUnder = leg.overUnder!.toLowerCase();
          const recExplain = splitSignal.recSupportsBet
            ? `Finds favorable historical patterns for this ${overUnder}.`
            : `Does not find strong patterns supporting this ${overUnder}.`;
          const edgeExplain = splitSignal.edgeSupportsBet
            ? `Predicts the stat will ${leg.overUnder === 'Over' ? 'exceed' : 'fall below'} the line.`
            : `Predicts the stat will ${leg.overUnder === 'Over' ? 'fall below' : 'exceed'} the line.`;

          return (
            <Tooltip
              content={
                <>
                  <span className="font-semibold text-amber-400">Split Signal</span>
                  <br />
                  Two ML models disagree on this pick:
                  <br />
                  <br />
                  <span className="text-purple-300">
                    Pattern Model ({splitSignal.recommendation}):
                  </span>{' '}
                  {recExplain}
                  <br />
                  <span className="text-purple-300">
                    Value Model ({edgeSign}
                    {splitSignal.edge.toFixed(1)}):
                  </span>{' '}
                  {edgeExplain}
                  <br />
                  <br />
                  Use your judgment — consider other signals and context.
                </>
              }
            >
              <span
                data-testid="ml-split-signal-badge"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/30 cursor-help"
              >
                <GitCompare className="w-3 h-3" />
                SPLIT
              </span>
            </Tooltip>
          );
        })()}

      {/* ML Top Factors: admin only, inline text rather than a badge */}
      {leg.mlTopFactors && leg.mlTopFactors.length > 0 && !factorsHidden && (
        <Tooltip
          content={
            <>
              <span className="font-semibold text-purple-300">Top ML Factors</span>
              <br />
              {leg.mlTopFactors.slice(0, 3).map((f, i) => (
                <span key={i}>
                  {i + 1}. {formatFactorName(f.feature)}: {f.contribution}
                  <br />
                </span>
              ))}
            </>
          }
        >
          <span
            data-testid="ml-factors-badge"
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-500/20 text-purple-400 ring-1 ring-purple-500/30 cursor-help"
          >
            {leg.mlTopFactors
              .slice(0, 3)
              .map((f) => formatFactorName(f.feature))
              .join(' · ')}
          </span>
        </Tooltip>
      )}
    </>
  );
}

/** Format a feature name from snake_case to readable label */
function formatFactorName(feature: string): string {
  return feature
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bL(\d+)\b/g, 'L$1')
    .replace(/\bB2b\b/, 'B2B')
    .replace(/\bEv\b/, 'EV')
    .replace(/\bH2h\b/, 'H2H');
}
